<template>
  <main class="main px-5">
    <section>
        <div v-if="loading">
            <LoadingSpinner />
        </div>
        <div v-else class="mx-0 pb-5">
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                <h3 class="mb-0">Client Stats</h3>
            </div>

            <div class="w-100 input-group">
                <div class="d-flex align-items-center">
                    <label class="mb-0 mr-2">Year</label>
                    <select
                        v-model="selectedYear"
                        class="form-control"
                        style="width: auto; min-width: 100px;"
                        @change="getData()"
                    >
                        <option
                            v-for="y in availableYears"
                            :key="y"
                            :value="y"
                        >
                            {{ y }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-12 mt-5">
                    <h5 class="ml-3">Subscribed clients</h5>
                    <div>
                        <StatsTable
                            :statsClient="statsClientSubscribed"
                            :statsType="statsTypeSubscribedWithTooltips"
                            :statsDate="statsDate"
                            :showTotal="false"
                            :tooltip="true"
                        />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-12 mt-5">
                    <h5 class="ml-3">Non Subscribed clients</h5>
                    <div>
                        <StatsTable
                            :statsClient="statsClientNonSubscribed"
                            :statsType="statsTypeNonSubscribedWithTooltips"
                            :statsDate="statsDate"
                            :showTotal="false"
                            :tooltip="true"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</main>
</template>

<script>
import { mapActions } from "vuex";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { adminService } from "@/_services";
import StatsTable from "@/components/Usages/StatsTable.vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

function getValueByFdate(arr, fdateDisplay) {
    if (!arr || !arr.length) return null;
    const fdateApi = fdateDisplay.replace(/\//g, "-");
    const item = arr.find((x) => x.fdate === fdateApi);
    return item ? parseFloat(item.total) || 0 : null;
}

export default {
    data() {
        const currentYear = new Date().getFullYear();
        return {
            loading: true,
            selectedYear: currentYear,
            availableYears: [
                2020, 2021, 2022, 2023, 2024, 2025, 2026
            ],
            statsDate: [],
            statsClientSubscribed: {},
            statsClientNonSubscribed: {},
            statsTypeSubscribed: [
                { id: "new", label: "New clients", format: "qty", showQty: false, showTotal: false },
                //{ id: "reactivated", label: "Reactivated clients", format: "qty", showQty: false, showTotal: false },
                { id: "churned", label: "Churned clients", format: "qty", showQty: true, showTotal: false },
                { id: "total_subscribed", label: "Total subscribed clients", format: "qty", showQty: false, showTotal: false },
                { id: "paying", label: "Paying subscribed clients", format: "qty", showQty: false, showTotal: false },
            ],
            statsTypeNonSubscribed: [
                { id: "new", label: "New clients", format: "qty", showQty: false, showTotal: false },
                //{ id: "reactivated", label: "Reactivated clients", format: "qty", showQty: false, showTotal: false },
                { id: "churned", label: "Churned clients", format: "qty", showQty: true, showTotal: false },
                { id: "total_subscribed", label: "Total subscribed clients", format: "qty", showQty: false, showTotal: false },
                { id: "paying", label: "Paying subscribed clients", format: "qty", showQty: false, showTotal: false },
            ],
        };
    },
    components: {
        LoadingSpinner,
        StatsTable,
        Treeselect,
    },
    mounted() {
        this.setCurrentRoute({ page: "clients" });
        this.setCurrentRouteChild({ child: "Client Stats" });
    },
    created() {
        this.getData();
    },
    computed: {
        statsTypeSubscribedWithTooltips() {
            return this.statsTypeSubscribed.map((item) => ({
                ...item,
                tooltip: this.$t("lang.label." + item.id + "_tooltip_subscribed"),
            }));
        },
        statsTypeNonSubscribedWithTooltips() {
            return this.statsTypeNonSubscribed.map((item) => ({
                ...item,
                tooltip: this.$t("lang.label." + item.id + "_tooltip_non_subscribed"),
            }));
        },
    },
    methods: {
        ...mapActions("headertopbar", {
            setCurrentRoute: "setRoute",
            setCurrentRouteChild: "setRouteChild",
        }),
        getData() {
            this.loading = true;
            adminService.getClientStatsByMonth(this.selectedYear).then((response) => {
                const data = response.data != null ? response.data : response;
                if (data && data.subscribed_clients && data.non_subscribed_clients) {
                    this.buildStats(data);
                }
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        buildStats(data) {
            const subs = data.subscribed_clients;
            const nonSubs = data.non_subscribed_clients;

            const dateSet = new Set();
            ["new", "churned", "reactivated", "paying", "total_prev_month"].forEach((key) => {
                (subs[key] || []).forEach((item) => {
                    if (item.fdate) dateSet.add(item.fdate.replace(/-/g, "/"));
                });
                (nonSubs[key] || []).forEach((item) => {
                    if (item.fdate) dateSet.add(item.fdate.replace(/-/g, "/"));
                });
            });
            const sortedMonths = Array.from(dateSet).sort((a, b) => {
                const [yA, mA] = a.split("/").map(Number);
                const [yB, mB] = b.split("/").map(Number);
                return yA !== yB ? yA - yB : mA - mB;
            });

            this.statsDate = sortedMonths.map((date) => ({ date }));

            const clientId = 0;
            this.statsClientSubscribed = this.buildOneClient(subs, sortedMonths, clientId);
            this.statsClientNonSubscribed = this.buildOneClient(nonSubs, sortedMonths, clientId);
        },
        buildOneClient(section, sortedMonths, clientId) {
            const statsClient = {};
            statsClient[clientId] = { name: "" };

            sortedMonths.forEach((fdateDisplay) => {
                const newVal = getValueByFdate(section.new, fdateDisplay);
                const reactivatedVal = getValueByFdate(section.reactivated, fdateDisplay);
                const churnedVal = getValueByFdate(section.churned, fdateDisplay);
                const payingVal = getValueByFdate(section.paying, fdateDisplay);

                const payingNum = payingVal != null ? parseFloat(payingVal) : 0;
                const churnedNum = churnedVal != null ? parseFloat(churnedVal) : 0;
                const churnedPct = payingNum > 0 && churnedVal != null
                    ? ((churnedNum / payingNum) * 100).toFixed(0) + " %"
                    : (churnedVal != null ? "0 %" : undefined);
                statsClient[clientId][fdateDisplay] = {
                    new_qty: newVal != null ? newVal : undefined,
                    reactivated_qty: reactivatedVal != null ? reactivatedVal : undefined,
                    churned_qty: churnedVal != null ? churnedVal : undefined,
                    paying_qty: payingVal != null ? payingVal : undefined,
                    churned: churnedPct,
                };
            });

            const raw = section.total_prev_month;
            const totalPrevMonthValue = !raw ? 0 : Array.isArray(raw) && raw.length
                ? parseFloat(raw[0].total) || 0
                : (typeof raw.total !== "undefined" ? parseFloat(raw.total) || 0 : 0);
            let prevTotal = totalPrevMonthValue;
            sortedMonths.forEach((fdateDisplay) => {
                const row = statsClient[clientId][fdateDisplay];
                const baseVal = prevTotal;
                const newVal = row.new_qty != null ? row.new_qty : 0;
                const reactivatedVal = 0;
                const churnedVal = row.churned_qty != null ? row.churned_qty : 0;
                const total = baseVal + newVal + reactivatedVal - churnedVal;
                row.total_subscribed_qty = total;
                prevTotal = total;
            });

            return statsClient;
        },
    },
}
</script>